import { useNavigate, useSearch } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import type { ReactNode } from "react";

import { DataTable } from "@/components/data";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import type { Page } from "../api";
import { Chip, FlagChip } from "../chips";

/** Rows per page on every paged table (mockup v6 pager). */
export const PAGE_SIZE = 25;
export const PER_CHOICES = [10, 25] as const;
/** The minimum standard every rating bar is read against. */
export const MIN_STANDARD = 7;

export const f1 = (value: number | null | undefined): string => (value == null ? "—" : value.toFixed(1));
export const f2 = (value: number | null | undefined): string => (value == null ? "—" : value.toFixed(2));

export function money(value: number | null | undefined, dp = 2): string {
  if (value == null) return "—";
  return `£${value.toLocaleString("en-GB", { minimumFractionDigits: dp, maximumFractionDigits: dp })}`;
}

/** Fixed month names: the browser's own short names differ ("Sep" in one, "Sept" in another). */
export const MONTHS: readonly string[] = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "24 Sep 2026" from an ISO date or timestamp, read as UTC. */
export function dateGB(iso: string): string {
  const date = new Date(iso);
  return `${date.getUTCDate()} ${MONTHS[date.getUTCMonth()]} ${date.getUTCFullYear()}`;
}

/** "Sep 2026" from an ISO date, read as UTC. */
export function monthGB(iso: string): string {
  const date = new Date(iso);
  return `${MONTHS[date.getUTCMonth()]} ${date.getUTCFullYear()}`;
}

export interface Kpi {
  label: string;
  value: ReactNode;
  note?: ReactNode;
  danger?: boolean;
}

/** The head row of every page: its name, a grey sub-line and an optional figure top right. */
export function PageHead({ title, sub, kpi }: { title: string; sub?: ReactNode; kpi?: Kpi | null }) {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-3">
      <div>
        <h2 className="text-lg font-bold">{title}</h2>
        {sub ? <p className="mt-1 text-xs text-ks-grey-1">{sub}</p> : null}
      </div>
      {kpi ? (
        <div className="text-right text-xs text-ks-grey-1">
          {kpi.label}
          <b className={cn("block text-2xl", kpi.danger ? "text-ks-danger" : "text-ks-ink")}>{kpi.value}</b>
          {kpi.note ? <span className="ml-auto block max-w-sm text-xs text-ks-ink">{kpi.note}</span> : null}
        </div>
      ) : null}
    </div>
  );
}

/** The page number lives in the URL as `page` (1-based). It is not an `f_` key, so it does not follow a tab switch. */
export function pageOf(search: Record<string, unknown>, prefix = ""): number {
  const raw = Number(search[`${prefix}page`]);
  return Number.isInteger(raw) && raw >= 1 ? raw : 1;
}

export function pageSearch(previous: Record<string, unknown>, next: number, prefix = ""): Record<string, unknown> {
  const out = { ...previous };
  const key = `${prefix}page`;
  if (next <= 1) delete out[key];
  else out[key] = next;
  return out;
}

export function usePage(prefix = ""): [number, (next: number) => void] {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const navigate = useNavigate();
  const setPage = (next: number) =>
    void navigate({
      search: ((previous: Record<string, unknown>) => pageSearch(previous, next, prefix)) as never,
    } as never);
  return [pageOf(search, prefix), setPage];
}

export function perOf(search: Record<string, unknown>, prefix = ""): 10 | 25 {
  const raw = search[`${prefix}per`];
  return raw === 25 || raw === "25" ? 25 : 10;
}

export function usePer(prefix = ""): [10 | 25, (next: 10 | 25) => void] {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const navigate = useNavigate();
  const setPer = (next: 10 | 25) =>
    void navigate({
      search: ((previous: Record<string, unknown>) => {
        const out = { ...previous, [`${prefix}per`]: next };
        delete out[`${prefix}page`];
        return out;
      }) as never,
    } as never);
  return [perOf(search, prefix), setPer];
}

/** A header sort on a paged table. The API sorts the whole list (?sort=&dir=), so every page follows it. */
export type TableSort = { id: string; direction: "asc" | "desc" } | null;

export function sortOf(search: Record<string, unknown>, prefix = ""): TableSort {
  const sortKey = `${prefix}sort`;
  const dirKey = `${prefix}dir`;
  const id = typeof search[sortKey] === "string" && search[sortKey] !== "" ? search[sortKey] : null;
  if (id === null) return null;
  return { id, direction: search[dirKey] === "desc" ? "desc" : "asc" };
}

/** One header click: a new column starts A to Z (low to high), then Z to A, then back to the original order. */
export function nextSort(current: TableSort, id: string): TableSort {
  if (current === null || current.id !== id) return { id, direction: "asc" };
  return current.direction === "asc" ? { id, direction: "desc" } : null;
}

/** A new sort starts again on page 1. Not an f_ filter, so it stays on this tab like the page number. */
export function sortSearch(previous: Record<string, unknown>, next: TableSort, prefix = ""): Record<string, unknown> {
  const out = { ...previous };
  const pageKey = `${prefix}page`;
  const sortKey = `${prefix}sort`;
  const dirKey = `${prefix}dir`;
  delete out[pageKey];
  delete out[sortKey];
  delete out[dirKey];
  if (next !== null) {
    out[sortKey] = next.id;
    out[dirKey] = next.direction;
  }
  return out;
}

export function sortParams(sort: TableSort): Record<string, string> {
  return sort === null ? {} : { sort: sort.id, dir: sort.direction };
}

export function useTableSort(prefix = ""): [TableSort, (id: string) => void] {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const navigate = useNavigate();
  const sort = sortOf(search, prefix);
  const onSort = (id: string) =>
    void navigate({
      search: ((previous: Record<string, unknown>) => sortSearch(previous, nextSort(sortOf(previous, prefix), id), prefix)) as never,
    } as never);
  return [sort, onSort];
}

export function pagerLabel(page: number, total: number, per = PAGE_SIZE): string {
  if (total === 0) return "0 of 0";
  return `${(page - 1) * per + 1}–${Math.min(total, page * per)} of ${total}`;
}

/** "1–25 of 219 ‹ ›". Both buttons change nothing, so a view-only user keeps them. */
export function Pager({
  page,
  total,
  per = PAGE_SIZE,
  onPage,
  onPer,
}: {
  page: number;
  total: number;
  per?: number;
  onPage: (next: number) => void;
  onPer?: (next: 10 | 25) => void;
}) {
  const pages = Math.max(1, Math.ceil(total / per));
  return (
    <nav aria-label="Pages" className="mt-2 flex items-center justify-end gap-2 text-xs text-ks-grey-1">
      <span>{pagerLabel(page, total, per)}</span>
      <Button data-nonmutating type="button" variant="outline" size="sm" aria-label="Previous page" disabled={page <= 1} onClick={() => onPage(page - 1)}>
        ‹
      </Button>
      <Button data-nonmutating type="button" variant="outline" size="sm" aria-label="Next page" disabled={page >= pages} onClick={() => onPage(page + 1)}>
        ›
      </Button>
      {onPer ? (
        <Button data-nonmutating type="button" variant="outline" size="sm" onClick={() => onPer(per === 10 ? 25 : 10)}>
          Show {per === 10 ? 25 : 10}
        </Button>
      ) : null}
    </nav>
  );
}

/** Shown when a shared link asks for a page past the last row. */
export function PastEnd({ onFirst }: { onFirst: () => void }) {
  return (
    <div className="flex flex-col items-center gap-2 py-8">
      <p>This page is past the last row.</p>
      <Button data-nonmutating type="button" variant="outline" onClick={onFirst}>
        Go to the first page
      </Button>
    </div>
  );
}

/**
 * One page of rows in the mockup's table style, with the module pager under it.
 * The shell table draws its own pager only above one page, so it is handed
 * exactly the rows on screen and never draws one.
 */
export function PagedTable<Row>({
  columns,
  data,
  page,
  onPage,
  caption,
  hidePager = false,
  wrap,
  rowClassName,
  onRowClick,
  sort,
  onSort,
  per = PAGE_SIZE,
  onPer,
}: {
  columns: ColumnDef<Row, unknown>[];
  data: Page<Row>;
  page: number;
  onPage: (next: number) => void;
  caption: string;
  hidePager?: boolean;
  wrap?: boolean;
  rowClassName?: (row: Row) => string | undefined;
  onRowClick?: (row: Row) => void;
  /** A column marked `sortable` gets a header button; the caller sends the sort to the API. */
  sort?: TableSort;
  onSort?: (id: string) => void;
  per?: number;
  onPer?: (next: 10 | 25) => void;
}) {
  if (data.items.length === 0) return <PastEnd onFirst={() => onPage(1)} />;
  return (
    <>
      <div className="overflow-x-auto">
        <DataTable<Row>
          columns={columns}
          rows={data.items}
          page={{ pageIndex: 0, pageSize: Math.max(1, data.items.length), rowCount: data.items.length }}
          onPageChange={() => {}}
          density="catalogue"
          caption={caption}
          wrap={wrap}
          rowClassName={rowClassName}
          onRowClick={onRowClick}
          sort={sort ?? undefined}
          onSort={onSort}
        />
      </div>
      {!hidePager ? <Pager page={page} total={data.total} per={per} onPage={onPage} onPer={onPer} /> : null}
    </>
  );
}

/** Dropped and archived rows read in grey. */
export const MUTED_ROW = "text-ks-grey-1";

export const isInactive = (row: { dropped: boolean; phase: string | null }) => row.dropped || row.phase === "ARCHIVE";

/** The flag chips beside an option: Not scored or Low evidence, then Dropped or Archive. */
export function OptionFlags({ row }: { row: { not_scored: boolean; low_evidence: boolean; dropped: boolean; phase: string | null } }) {
  return (
    <span className="flex flex-wrap gap-1">
      {row.not_scored ? <FlagChip kind="not_scored" /> : row.low_evidence ? <FlagChip kind="low_evidence" /> : null}
      {row.dropped ? <FlagChip kind="dropped" /> : row.phase === "ARCHIVE" ? <FlagChip kind="archive" /> : null}
    </span>
  );
}

export function RefLegend() {
  return (
    <span className="flex items-center gap-1.5 text-xs font-normal normal-case tracking-normal text-ks-grey-1">
      <i aria-hidden="true" className="inline-block w-4 border-t-2 border-dashed border-ks-ink" />
      Min standard 7.0
    </span>
  );
}

/** A 0 to 10 rating as a bar: grey under the 7.0 standard, with the dashed 7.0 line. */
export function RatingBar({ value, className }: { value: number | null; className?: string }) {
  if (value == null) return <Chip tone="muted">Not scored</Chip>;
  const width = Math.max(0, Math.min(10, value)) * 10;
  return (
    <div role="img" aria-label={`Rating ${f2(value)} of 10`} className={cn("relative h-4 min-w-44", className)}>
      <div
        className={cn("h-full rounded-[2px]", value < MIN_STANDARD ? "bg-ks-grey-1" : "bg-ks-purple")}
        style={{ width: `${width}%` }}
      />
      <div className="absolute -top-1 -bottom-1 border-l-2 border-dashed border-ks-ink" style={{ left: "70%" }} />
    </div>
  );
}

/** A quiet boxed note (mockup `.note`): purple left edge, used for "why this page is empty" messages. */
export function Note({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-[8px] border border-l-4 border-ks-grey-3 border-l-ks-purple bg-ks-surface px-3.5 py-2.5 text-sm">
      {children}
    </div>
  );
}

export function Footnote({ children }: { children: ReactNode }) {
  return <div className="mt-2 text-xs text-ks-grey-1">{children}</div>;
}
